import React, { useEffect, useMemo, useState } from 'react';
import { X, Check, AlertTriangle, Globe, Building2, Landmark, Upload, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { legalEntitiesAPI, saleInvoicesAPI } from '../../services/api';
import { SUPPORTED_CURRENCIES } from '../../utils/currency';
import './EInvoiceConfigModal.css';

const FISCAL_ID_PLACEHOLDER = {
  siren: '842512345',
  siret: '84251234500018',
  tva_ic: 'FR42842512345',
  rcs: 'Paris B 842 512 345',
  code_ape: '6202A',
  enterprise_number: '0123.456.789',
  vat_number: 'BE0123456789',
  nif: '000216001234567',
  nis: '0982160012345',
  rc: '16/00-1234567B08',
  article_imposition: '16050123456',
  matricule_fiscal: '1234567/A/M/000',
};

const TRANSPORT_LABEL = {
  MANUAL: 'Téléchargement manuel',
  PEPPOL: 'Réseau Peppol',
  PDP: 'Plateforme de Dématérialisation Partenaire',
  TTN: 'TTN El-Fatoora',
};

export default function EInvoiceConfigModal({ project, onClose, onSaved }) {
  const [entities, setEntities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [entityRes, countryRes] = await Promise.all([
          legalEntitiesAPI.list(),
          saleInvoicesAPI.countries(),
        ]);
        setEntities(entityRes.data);
        setCountries(countryRes.data);
        const preferred =
          entityRes.data.find((e) => e.country_code === (project?.pays || '').slice(0, 2).toUpperCase()) ||
          entityRes.data[0];
        if (preferred) setSelectedId(preferred.id);
      } catch {
        toast.error('Impossible de charger les entités légales');
      } finally {
        setLoading(false);
      }
    })();
  }, [project]);

  const selected = useMemo(
    () => entities.find((e) => e.id === selectedId) || null,
    [entities, selectedId],
  );

  useEffect(() => {
    if (!selected) { setForm(null); return; }
    setForm({
      name: selected.name || '',
      legal_form: selected.legal_form || '',
      capital: selected.capital ?? '',
      address: selected.address || '',
      postal_code: selected.postal_code || '',
      city: selected.city || '',
      default_currency: selected.default_currency || 'EUR',
      default_vat_rate: selected.default_vat_rate ?? '',
      iban: selected.iban || '',
      bic: selected.bic || '',
      bank_name: selected.bank_name || '',
      invoice_prefix: selected.invoice_prefix || '',
      logo_url: selected.logo_url || '',
      legal_mentions: selected.legal_mentions || '',
      peppol_participant_id: selected.peppol_participant_id || '',
      pdp_identifier: selected.pdp_identifier || '',
      ttn_identifier: selected.ttn_identifier || '',
      fiscal_ids: { ...(selected.fiscal_ids || {}) },
    });
  }, [selected]);

  const requiredFiscalIds = selected?.profile?.required_fiscal_ids || [];
  const missingNow = requiredFiscalIds.filter(({ key }) => !form?.fiscal_ids?.[key]?.trim?.());

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setFiscalId = (key, value) =>
    setForm((f) => ({ ...f, fiscal_ids: { ...f.fiscal_ids, [key]: value } }));

  const handleSave = async () => {
    if (!selected || !form) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        capital: form.capital === '' ? null : parseFloat(form.capital),
        default_vat_rate: form.default_vat_rate === '' ? null : parseFloat(form.default_vat_rate),
      };
      const { data } = await legalEntitiesAPI.update(selected.id, payload);
      setEntities((list) => list.map((e) => (e.id === data.id ? data : e)));
      toast.success('Configuration enregistrée');
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const transport = selected?.transport;

  return (
    <div className="eic-overlay" onClick={onClose}>
      <div className="eic-modal" onClick={(e) => e.stopPropagation()}>
        <header className="eic-header">
          <div>
            <h2>Configuration facture électronique</h2>
            <p className="eic-sub">{project?.nom || 'Projet'}</p>
          </div>
          <button className="eic-close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
        </header>

        {loading ? (
          <div className="eic-body"><p className="eic-muted">Chargement…</p></div>
        ) : !selected ? (
          <div className="eic-body"><p className="eic-muted">Aucune entité légale configurée.</p></div>
        ) : (
          <div className="eic-body">
            <section className="eic-section">
              <label className="eic-label"><Building2 size={13} /> Entité émettrice</label>
              <div className="eic-entity-tabs">
                {entities.map((e) => (
                  <button
                    key={e.id}
                    className={`eic-entity-tab ${e.id === selectedId ? 'active' : ''}`}
                    onClick={() => setSelectedId(e.id)}
                  >
                    {e.code}
                    {e.missing_fiscal_ids?.length > 0 && <span className="eic-dot" />}
                  </button>
                ))}
              </div>
            </section>

            {selected.profile && (
              <div className="eic-profile">
                <Globe size={14} />
                <div>
                  <strong>{selected.profile.label}</strong> — format {selected.profile.document_format}
                  <div className="eic-muted">
                    Canal : {TRANSPORT_LABEL[transport?.channel] || transport?.channel || '—'}
                    {transport && (
                      transport.configured
                        ? <span className="eic-ok"> · configuré</span>
                        : <span className="eic-warn"> · identifiants manquants ({transport.missing_configuration.join(', ')})</span>
                    )}
                  </div>
                  <div className="eic-muted">
                    Taux de TVA applicables : {selected.profile.vat_rates.map((r) => `${r} %`).join(' · ')}
                  </div>
                </div>
              </div>
            )}

            {missingNow.length > 0 && (
              <div className="eic-alert">
                <AlertTriangle size={14} />
                <div>
                  <strong>{missingNow.length} identifiant(s) fiscal(aux) obligatoire(s) manquant(s)</strong>
                  <div className="eic-muted">{missingNow.map((m) => m.label).join(' · ')}</div>
                </div>
              </div>
            )}

            {form && (
              <>
                <section className="eic-section">
                  <label className="eic-label">Identité légale</label>
                  <div className="eic-grid">
                    <Field label="Raison sociale" value={form.name} onChange={(v) => setField('name', v)} />
                    <Field label="Forme juridique" value={form.legal_form} onChange={(v) => setField('legal_form', v)} placeholder="SAS, SARL…" />
                    <Field label="Capital" type="number" value={form.capital} onChange={(v) => setField('capital', v)} />
                    <Field label="Préfixe de numérotation" value={form.invoice_prefix} onChange={(v) => setField('invoice_prefix', v)} placeholder="WK-FR" />
                    <LogoField value={form.logo_url} onChange={(v) => setField('logo_url', v)} />
                    <Field label="Adresse" value={form.address} onChange={(v) => setField('address', v)} wide />
                    <Field label="Code postal" value={form.postal_code} onChange={(v) => setField('postal_code', v)} />
                    <Field label="Ville" value={form.city} onChange={(v) => setField('city', v)} />
                    <div className="eic-field">
                      <label>Devise par défaut</label>
                      <select value={form.default_currency} onChange={(e) => setField('default_currency', e.target.value)}>
                        {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <Field label="TVA par défaut (%)" type="number" value={form.default_vat_rate} onChange={(v) => setField('default_vat_rate', v)} />
                  </div>
                </section>

                <section className="eic-section">
                  <label className="eic-label">Identifiants fiscaux — {selected.profile?.label}</label>
                  <div className="eic-grid">
                    {requiredFiscalIds.map(({ key, label }) => (
                      <Field
                        key={key}
                        label={label}
                        required
                        value={form.fiscal_ids[key] || ''}
                        onChange={(v) => setFiscalId(key, v)}
                        placeholder={FISCAL_ID_PLACEHOLDER[key]}
                      />
                    ))}
                  </div>
                </section>

                <section className="eic-section">
                  <label className="eic-label"><Landmark size={13} /> Coordonnées bancaires</label>
                  <div className="eic-grid">
                    <Field label="IBAN" value={form.iban} onChange={(v) => setField('iban', v)} wide />
                    <Field label="BIC" value={form.bic} onChange={(v) => setField('bic', v)} />
                    <Field label="Banque" value={form.bank_name} onChange={(v) => setField('bank_name', v)} />
                  </div>
                </section>

                <section className="eic-section">
                  <label className="eic-label">Transmission</label>
                  <div className="eic-grid">
                    <Field label="Identifiant Peppol" value={form.peppol_participant_id} onChange={(v) => setField('peppol_participant_id', v)} placeholder="0208:0123456789" />
                    <Field label="Identifiant PDP" value={form.pdp_identifier} onChange={(v) => setField('pdp_identifier', v)} />
                    <Field label="Identifiant TTN" value={form.ttn_identifier} onChange={(v) => setField('ttn_identifier', v)} />
                  </div>
                </section>

                <section className="eic-section">
                  <label className="eic-label">Mentions légales complémentaires</label>
                  <textarea
                    className="eic-textarea"
                    rows={3}
                    value={form.legal_mentions}
                    onChange={(e) => setField('legal_mentions', e.target.value)}
                    placeholder="Mentions ajoutées au bas de chaque facture émise par cette entité"
                  />
                </section>
              </>
            )}
          </div>
        )}

        <footer className="eic-footer">
          <button className="eic-btn" onClick={onClose}>Annuler</button>
          <button className="eic-btn primary" onClick={handleSave} disabled={saving || !form}>
            <Check size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </div>
    </div>
  );
}

const LOGO_MAX_BYTES = 400 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/gif', 'image/webp'];

function LogoField({ value, onChange }) {
  const inputRef = React.useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error('Format non supporte - PNG, JPG, SVG, GIF ou WebP');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error(`Fichier trop lourd (${Math.round(file.size / 1024)} Ko, maximum 400 Ko)`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.onerror = () => toast.error('Lecture du fichier impossible');
    reader.readAsDataURL(file);
  };

  return (
    <div className="eic-field wide">
      <label>Logo affiche sur la facture</label>
      <div className="eic-logo-row">
        <div className="eic-logo-preview">
          {value
            ? <img src={value} alt="Logo" />
            : <span className="eic-logo-empty">Aucun logo</span>}
        </div>
        <div className="eic-logo-actions">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          />
          <button type="button" className="eic-btn" onClick={() => inputRef.current?.click()}>
            <Upload size={13} /> Choisir un fichier
          </button>
          {value && (
            <button type="button" className="eic-btn" onClick={() => onChange('')}>
              <Trash2 size={13} /> Retirer
            </button>
          )}
          <div className="eic-logo-hint">PNG, JPG, SVG - 400 Ko max - s'affiche en haut a gauche de la facture</div>
        </div>
      </div>
      <input
        type="text"
        value={value && value.startsWith('data:') ? '' : (value || '')}
        placeholder="...ou coller une URL https://"
        onChange={(e) => onChange(e.target.value)}
        style={{ marginTop: 6 }}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, required, wide }) {
  return (
    <div className={`eic-field ${wide ? 'wide' : ''}`}>
      <label>{label}{required && <span className="eic-req"> *</span>}</label>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={required && !value ? 'missing' : ''}
      />
    </div>
  );
}
